<template>
  <div class="material-stories-modal">
    <div
      class="material-stories-modal__substrate"
      @click="$emit('close')"
    />

    <button
      class="m-btn material-stories-modal__mute"
      @click="videoMuted = !videoMuted"
    >
      <img
        v-show="videoMuted"
        :key="`mute-${videoMuted}`"
        src="/img/materials/stories/mute.svg"
        alt="mute"
      >
      <img
        v-show="!videoMuted"
        :key="`unmute-${videoMuted}`"
        src="/img/materials/stories/unmute.svg"
        alt="mute"
      >
      <span>{{ videoMuted ? 'выключено' : 'включено' }}</span>
    </button>

    <button
      class="m-btn material-stories-modal__close"
      @click="$emit('close')"
    >
      <img
        src="/img/materials/stories/close.svg"
        alt="close"
        class="no-mobile-adaptive"
      >
      <img
        src="/img/materials/stories/close-mobile.svg"
        alt="close"
        class="mobile-adaptive"
      >
    </button>

    <div
      class="material-stories-modal__slider"
      :style="{ transform: `translateX(${sliderTransform}px)` }"
    >
      <div
        v-for="(story, index) in stories"
        :key="story.id"
        :class="['material-stories-modal__story', {
          'material-stories-modal__current': index === currentStoryIndex
        }]"
        @touchstart="onTouchStart($event, index)"
        @touchend="onTouchEnd($event, index)"
        @click="chooseStory(index)"
      >
        <div
          v-if="index === currentStoryIndex"
          class="video__lines"
        >
          <div
            v-for="(video, idx) in stories[currentStoryIndex].videos"
            :key="idx"
            class="video__line"
          >
            <div
              :class="['video__line_fill', { 'animated': idx === currentVideoIndex }]"
              :style="getFillStyle(idx)"
            />
          </div>
        </div>
        <div class="material-stories-modal__story_header">
          <img
            :src="story.img"
            :alt="story.text"
          >
          {{ story.text }}
        </div>
        <video
          :ref="`video-${index}`"
          :src="index === currentStoryIndex ? currentVideo.src : story.videos[0].src"
          :autoplay="index === currentStoryIndex"
          :muted="videoMuted"
          playsinline
          @ended="onEndedVideo"
        />
        <div
          v-if="index === currentStoryIndex"
          class="material-stories-modal__story_btns"
        >
          <div
            class="material-stories-modal__story_prev"
            @touchstart.stop
            @click.stop="prevVideo"
          />
          <div
            class="material-stories-modal__story_next"
            @touchstart.stop
            @click.stop="nextVideo"
          />
        </div>
        <nuxt-link
          v-if="index === currentStoryIndex && currentVideo.link"
          :to="currentVideo.link"
        >
          {{ currentVideo.text }}
        </nuxt-link>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MaterialsStoriesModal',
  props: {
    startStoryIndex: {
      type: Number,
      default: 0,
    },
    stories: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      currentStoryIndex: this.startStoryIndex,
      currentVideoIndex: 0,
      videoMuted: true,
      sliderTransform: 0,

      startY: 0,
      startX: 0,
    };
  },
  computed: {
    currentStory() {
      return this.stories[this.currentStoryIndex];
    },
    currentVideo() {
      return this.currentStory.videos[this.currentVideoIndex];
    },
  },
  watch: {
    currentStoryIndex(newIndex, oldIndex) {
      this.$refs[`video-${oldIndex}`][0].pause();
      this.$refs[`video-${oldIndex}`][0].currentTime = 0;
      this.$refs[`video-${newIndex}`][0].play();
      setTimeout(() => {
        this.setSliderPosition();
      }, 50);
    },
  },
  mounted() {
    this.setSliderPosition();
  },
  methods: {
    chooseStory(index) {
      if (index < 0) return;
      if (index > this.stories.length - 1) return;

      this.currentStoryIndex = index;
      this.currentVideoIndex = 0;
    },
    onEndedVideo() {
      if (this.currentVideoIndex !== (this.stories[this.currentStoryIndex].videos.length - 1)) {
        this.currentVideoIndex += 1;
        return;
      }

      if (this.currentStoryIndex !== (this.stories.length - 1)) {
        this.chooseStory(this.currentStoryIndex + 1);
        return;
      }

      this.currentStoryIndex = 0;
      this.currentVideoIndex = 0;
    },
    getFillStyle(index) {
      if (index < this.currentVideoIndex) {
        return {
          width: '100%',
          animation: null,
        };
      }
      if (index === this.currentVideoIndex) {
        return {
          animationDuration: `${this.currentVideo.duration}s`,
        };
      }
      return {
        width: '0',
        animation: null,
      };
    },
    setSliderPosition() {
      const pageWidth = window.innerWidth;
      const active = document.querySelector('.material-stories-modal__current');
      const activeRect = active.getBoundingClientRect();

      this.sliderTransform += (pageWidth / 2) - (activeRect.width / 2) - activeRect.left;
    },
    prevVideo() {
      if (this.currentVideoIndex > 0) {
        this.currentVideoIndex -= 1;
        return;
      }
      if (this.currentStoryIndex > 0) {
        this.chooseStory(this.currentStoryIndex - 1);
      }
    },
    nextVideo() {
      if (this.currentStory.videos.length - 1 !== this.currentVideoIndex) {
        this.currentVideoIndex += 1;
        return;
      }
      if (this.stories.length - 1 !== this.currentStoryIndex) {
        this.chooseStory(this.currentStoryIndex + 1);
      }
    },

    onTouchStart(event, index) {
      if (index !== this.currentStoryIndex) return;

      const touch = event.touches[0];
      this.startY = touch.clientY;
      this.startX = touch.clientX;
    },
    onTouchEnd(event, index) {
      if (index !== this.currentStoryIndex) return;

      const touch = event.changedTouches[0];

      const endY = touch.clientY;
      const swipeYDistance = endY - this.startY;

      if (this.startY && swipeYDistance > 100) {
        this.$emit('close');
        return;
      }

      const endX = touch.clientX;
      const swipeXDistance = endX - this.startX;

      if (this.startX && swipeXDistance > 100) {
        this.chooseStory(this.currentStoryIndex - 1);
        return;
      }
      if (this.startX && swipeXDistance < -100) {
        this.chooseStory(this.currentStoryIndex + 1);
      }
    },
  },
};
</script>

<style scoped lang="scss">

.material-stories-modal {
  position: fixed;
  inset: 0;
  z-index: 10;
  overflow: hidden;
  display: grid;
  place-content: center;

  &__substrate {
    position: absolute;
    inset: 0;
    background-color: rgba(4, 2, 18, 0.6);
    backdrop-filter: blur(calc(var(--scale) * 5));

    @include media-down($size-mobile) {
      background-color: #000000;
    }
  }

  &__mute {
    display: flex;
    align-items: center;
    gap: calc(var(--scale) * 12);
    position: absolute;
    top: calc(var(--scale) * 30);
    left: calc(var(--scale) * 40);

    @include media-down($size-tablet) {
      z-index: 3;
      top: auto;
      left: calc(var(--scale) * 20);
      bottom: calc(var(--scale) * 20);
    }

    img {
      width: calc(var(--scale) * 44);
      height: calc(var(--scale) * 44);

      @include media-down($size-tablet) {
        width: calc(var(--scale) * 24);
        height: calc(var(--scale) * 24);
      }
    }

    span {
      font-weight: 500;
      font-size: calc(var(--scale) * 14);
      line-height: 130%;
      color: #FFFFFF;

      @include media-down($size-mobile) {
        display: none;
      }
    }
  }

  &__close {
    position: absolute;
    top: calc(var(--scale) * 30);
    right: calc(var(--scale) * 40);

    @include media-down($size-mobile) {
      top: calc(var(--scale) * 16);
      right: calc(var(--scale) * 7);
      z-index: 3;
    }

    img {
      width: calc(var(--scale) * 44);
      height: calc(var(--scale) * 44);
    }
  }

  &__slider {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    transition: 200ms;
  }

  &__story {
    position: relative;
    width: calc(var(--scale) * 338);
    height: calc(var(--scale) * 600);
    flex-shrink: 0;
    transition: 200ms;
    margin-inline: calc(var(--scale) * 10);

    @include media-down($size-mobile) {
      width: 100vw;
      height: 100dvh;
      margin: 0;
      opacity: 0;
    }

    &_header {
      display: flex;
      align-items: center;
      gap: calc(var(--scale) * 8);
      position: absolute;
      z-index: 3;
      left: calc(var(--scale) * 16);
      top: calc(var(--scale) * 20);
      transition: 200ms;

      font-weight: 500;
      font-size: calc(var(--scale) * 13);
      line-height: 130%;
      color: #FFFFFF;

      img {
        width: calc(var(--scale) * 32);
        height: calc(var(--scale) * 32);
        border-radius: 50%;
      }
    }

    &_btns {
      display: flex;
      justify-content: space-between;
      position: absolute;
      inset: 0;

      background:
        linear-gradient(180deg,
          rgba(0, 0, 0, 0.5) 0%,
          rgba(0, 0, 0, 0) 8%,
          rgba(0, 0, 0, 0) 92%,
          rgba(0, 0, 0, 0.5) 100%);
      border-radius: calc(var(--scale) * 20);

      @include media-down($size-mobile) {
        border-radius: 0;
      }
    }

    &_prev {
      width: 40%;
      height: 100%;
    }

    &_next {
      width: 60%;
      height: 100%;
    }

    video {
      width: 100%;
      height: 100%;
      position: relative;
      border-radius: calc(var(--scale) * 20);
      object-fit: cover;
      transition: 200ms;
      background-color: #000000;

      @include media-down($size-mobile) {
        border-radius: 0;
      }
    }

    &:not(&.material-stories-modal__current)::before {
      content: '';
      position: absolute;
      z-index: 2;
      inset: -1px;
      display: block;
      background-color: #040212;
      opacity: 0.6;
      border-radius: calc(var(--scale) * 20);
      transition: 200ms;

      @include media-down($size-mobile) {
        content: none;
      }
    }

    &:hover::before {
      opacity: 0 !important;
    }
  }

  &__current {
    width: calc(var(--scale) * 405);
    height: calc(var(--scale) * 720);
    margin-inline: calc(var(--scale) * 130);
    transition: 200ms;

    @include media-down($size-tablet) {
      margin-inline: calc(var(--scale) * 50);
    }

    @include media-down($size-mobile) {
      width: 100vw;
      height: 100dvh;
      margin: 0;
      opacity: 1;
    }

    .material-stories-modal__story_header {
      gap: calc(var(--scale) * 10);
      left: calc(var(--scale) * 20);
      top: calc(var(--scale) * 22);
      font-size: calc(var(--scale) * 16);

      img {
        width: calc(var(--scale) * 32);
        height: calc(var(--scale) * 32);
        border-radius: 50%;
      }
    }

    a {
      position: absolute;
      left: 0;
      width: 100%;
      bottom: calc(-1 * var(--scale) * 46);

      font-weight: 400;
      font-size: calc(var(--scale) * 20);
      line-height: 130%;
      text-align: center;
      text-decoration-line: underline;
      color: #FFFFFF;

      @include media-down($size-mobile) {
        bottom: calc(var(--scale) * 20);

        font-weight: 400;
        font-size: calc(var(--scale) * 20);
        line-height: 130%;
        text-decoration-line: underline;
        color: #FFFFFF;
      }
    }
  }

  .video__lines {
    width: calc(var(--scale) * 365);
    display: flex;
    align-items: center;
    gap: calc(var(--scale) * 4);
    position: absolute;
    z-index: 2;
    top: calc(var(--scale) * 12);
    left: calc(var(--scale) * 20);

    @include media-down($size-mobile) {
      left: calc(var(--scale) * 16);
      width: calc(100% - var(--scale) * 32);
    }
  }

  .video__line {
    height: calc(var(--scale) * 2);
    width: 100%;
    position: relative;
    background-color: rgba(#FFFFFF, 0.5);
    border-radius: calc(var(--scale) * 10);
    overflow: hidden;

    &_fill {
      position: absolute;
      left: 0;
      top: 0;
      width: 0;
      height: 100%;
      background-color: #FFFFFF;

      &.animated {
        animation-name: fill-width;
        animation-iteration-count: 1;
        animation-fill-mode: forwards;
        animation-timing-function: linear;
      }
    }
  }
}

@keyframes fill-width {
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
}

</style>
